import { useState } from "react";

// Video selection, playback, chapters, and search presentation stay together.

// Leads with the video the AI picked and offers the rest as thumbnails.
//
// The lead is whichever card carries `primary` — NOT the first in the array.
// Vector similarity and "actually answers the question" are different things:
// the hands-on demo for a question often ranks below a conceptual video that
// merely shares vocabulary, which is why the backend re-ranks with an LLM.
//
// When no card is primary (the AI decision failed, or it picked an id outside
// the candidate list) every card is shown at equal weight instead — guessing a
// lead is the exact bug this field was added to fix.
export default function VideoCards({ videos, language }) {
  // Overrides the lead when someone picks a thumbnail. Null means "use primary".
  const [selectedId, setSelectedId] = useState(null);

  if (!videos?.length) return null;

  const primary = videos.find((v) => v.primary);
  const selected = selectedId
    ? videos.find((v) => v.video_id === selectedId)
    : null;
  const lead = selected ?? primary;

  // No AI pick and nobody clicked: show them flat rather than promoting one.
  if (!lead) {
    return (
      <div className="flex flex-col gap-3 mt-1">
        {videos.map((video, i) => (
          <VideoCard
            key={video.video_id ?? i}
            video={video}
            language={language}
          />
        ))}
      </div>
    );
  }

  const others = videos.filter((v) => v !== lead);

  return (
    <div className="flex flex-col gap-2 mt-1">
      <VideoCard key={lead.video_id} video={lead} language={language} />
      {others.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
            {language === "zh" ? "其他相關影片" : "Other related videos"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {others.map((video) => (
              <VideoMiniCard
                key={video.video_id}
                video={video}
                language={language}
                onSelect={() => setSelectedId(video.video_id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// One video: player, summary, and a chapter list that seeks the player.
//
// Chapters come from the backend for the WHOLE video, with exactly one flagged
// `matched` — the chapter the vector search actually hit, i.e. the reason this
// video is in the answer. That one starts expanded; the rest are collapsed, so
// the card opens on the part that answers the question.
function VideoCard({ video, language }) {
  const chapters = Array.isArray(video.chapters) ? video.chapters : [];
  const matchedIndex = Math.max(
    0,
    chapters.findIndex((c) => c.matched),
  );
  const [openChapter, setOpenChapter] = useState(matchedIndex);
  const [startAt, setStartAt] = useState(video.start_seconds ?? 0);
  // Remounts the iframe so a chapter click actually seeks; changing the src of a
  // live YouTube embed does not reliably reposition it.
  const [playerKey, setPlayerKey] = useState(0);

  const seekTo = (seconds) => {
    setStartAt(seconds ?? 0);
    setPlayerKey((k) => k + 1);
  };

  if (!video.video_id) return null;

  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 overflow-hidden">
      <div className="px-3 py-2 border-b border-black/10 dark:border-white/10 text-sm font-semibold text-gray-800 dark:text-gray-100">
        {video.title}
      </div>

      <div className="p-3 flex flex-col gap-3">
        <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg bg-black">
          <iframe
            key={playerKey}
            className="absolute top-0 left-0 w-full h-full border-0"
            src={`https://www.youtube.com/embed/${video.video_id}?start=${Math.floor(startAt)}`}
            title={video.title || "YouTube video player"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <a
          href={video.url}
          target="_blank"
          rel="noreferrer"
          className="self-start text-xs font-medium text-orange-700 dark:text-orange-400 hover:underline"
        >
          {language === "zh" ? "在 YouTube 觀看" : "Watch on YouTube"}
        </a>

        {video.content_scope === "title_only" && <p className="text-xs text-gray-500">
          {language === "zh" ? "僅依標題搜尋，無逐字稿或章節可供內容查證。" : "Matched by title only; no transcript or chapters are available."}
        </p>}
        {video.summary && (
          <div>
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
              {language === "zh" ? "影片摘要" : "Video Summary"}
            </div>
            <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              {video.summary}
            </p>
          </div>
        )}

        {chapters.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
              {language === "zh" ? "章節" : "Chapters"}
            </div>
            <ul className="flex flex-col gap-1">
              {chapters.map((chapter, i) => (
                <li
                  key={`${chapter.start_seconds}-${i}`}
                  className={`rounded-lg border ${
                    chapter.matched
                      ? "border-orange-500/60 bg-orange-50 dark:bg-orange-900/20"
                      : "border-black/10 dark:border-white/10"
                  }`}
                >
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => seekTo(chapter.start_seconds)}
                      className="text-[11px] font-mono tabular-nums text-orange-700 dark:text-orange-400 hover:underline shrink-0"
                      title={
                        language === "zh" ? "從這裡播放" : "Play from here"
                      }
                    >
                      {formatTimestamp(chapter.start_seconds)}
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenChapter(openChapter === i ? -1 : i)}
                      className="flex-1 text-left text-xs text-gray-800 dark:text-gray-100 min-w-0"
                    >
                      <span className="truncate block">{chapter.title}</span>
                    </button>
                    {chapter.matched && (
                      <span className="shrink-0 text-[10px] font-medium text-orange-700 dark:text-orange-400">
                        {language === "zh" ? "命中段落" : "matched"}
                      </span>
                    )}
                  </div>

                  {openChapter === i && chapter.transcript && (
                    <div className="px-2 pb-2">
                      <p className="text-[11px] leading-relaxed text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                        {chapter.transcript}
                      </p>
                      {/* Required by the backend contract: this text is raw ASR
                          with known mishearings, so it must never be presented
                          as an official transcript. The flag is always false
                          today and flips to true only once a human has checked
                          the text, at which point this note disappears on its
                          own. */}
                      {!chapter.transcript_verified && (
                        <p className="mt-1 text-[10px] italic text-gray-400 dark:text-gray-500">
                          {language === "zh"
                            ? "⚠ 自動語音辨識，未經校對，可能有誤字"
                            : "⚠ Auto-transcribed, unverified — may contain errors"}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// A compact row for the runner-up videos: thumbnail, title, and the chapter the
// search hit. Clicking promotes it into the player above rather than opening a
// second player, so only one video is ever playing.
function VideoMiniCard({ video, language, onSelect }) {
  const matched = video.chapters?.find((c) => c.matched);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex gap-2 items-start text-left rounded-lg border border-black/10 dark:border-white/10 bg-white/60 dark:bg-white/5 hover:border-orange-500/60 hover:bg-white dark:hover:bg-white/10 transition-colors p-2 w-full"
    >
      {video.video_id && (
        <img
          src={`https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`}
          alt=""
          loading="lazy"
          className="w-20 h-[45px] object-cover rounded shrink-0 bg-black"
        />
      )}
      <span className="flex flex-col min-w-0 gap-0.5">
        <span className="text-xs font-medium text-gray-800 dark:text-gray-100 line-clamp-2">
          {video.title}
        </span>
        {matched && (
          <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
            {formatTimestamp(matched.start_seconds)} · {matched.title}
          </span>
        )}
      </span>
    </button>
  );
}

function formatTimestamp(seconds) {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Rendered in both the empty and conversation layouts, which are separate JSX
// trees — keeping it in one component stops the two copies from drifting apart.
//
// The label names the source being searched rather than promising both media:
// when the library has a match the answer is the videos INSTEAD of the text, so
// wording like "include video" would read as a missing answer. The full
// behaviour is in the tooltip.
export function VideoSearchToggle({ checked, onChange, language }) {
  // A pill that fills with the page's orange when on, rather than a bare
  // checkbox — it sits next to the message bubbles and New Chat button, which
  // use the same orange-500 on white.
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      title={
        language === "zh"
          ? "勾選後將優先回覆相關影片；若沒有對應影片，會改以文字說明"
          : "Answers with video when we have one; falls back to text if we don't."
      }
      className={`inline-flex items-center gap-1.5 rounded-2xl border px-3 py-2 text-sm font-medium transition-colors ${
        checked
          ? "bg-orange-500 border-orange-500 text-white hover:bg-orange-600 hover:border-orange-600"
          : "bg-transparent border-black/20 dark:border-white/20 text-gray-600 dark:text-gray-300 hover:border-orange-500 hover:text-orange-600 dark:hover:text-orange-400"
      }`}
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="w-3.5 h-3.5 shrink-0"
        fill="currentColor"
      >
        <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h7A2.5 2.5 0 0 1 16 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 4 17.5v-11Zm14.4 2.1 2.5-1.6a.7.7 0 0 1 1.1.6v8.8a.7.7 0 0 1-1.1.6l-2.5-1.6a.8.8 0 0 1-.4-.7V9.3c0-.3.2-.6.4-.7Z" />
      </svg>
      {language === "zh" ? "站內影片搜尋" : "Video search"}
    </button>
  );
}

// The backend sends each video twice: once as a markdown bullet in the answer
// text, once as a structured card. When the cards render, drop the bullets that
// duplicate them — matching on the URL so only lines the cards actually cover
// are removed, and any other prose (the intro line, the "no video, using text
// instead" notice) survives untouched.
export function stripVideoList(message) {
  const text = message.text ?? "";
  const videos = message.videos;
  if (!videos?.length) return text;

  const ids = videos.map((v) => v.video_id).filter(Boolean);
  if (ids.length === 0) return text;

  return text
    .split("\n")
    .filter((line) => {
      const isBullet = /^\s*[-*]\s/.test(line);
      return !(isBullet && ids.some((id) => line.includes(id)));
    })
    .join("\n")
    .trim();
}
